import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ContactFormSection } from '@components/ui/ContactFormSection'
import { ImagePlaceholder } from '@components/ui/ImagePlaceholder'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { useDirectionList } from '@shared/hooks/useDirectionList'
import { useLang, withLang } from '@shared/i18n/useLang'
import { WP_URL } from '@shared/config/env'

interface NamedItem { title: string; desc: string }
interface Person { name: string; role: string }

export function HomePage() {
  const { directions } = useDirectionList()
  const { t } = useTranslation()
  const lang = useLang()

  const leadership = t('home.leadership', { returnObjects: true }) as Person[]
  const advantages = t('home.advantages', { returnObjects: true }) as NamedItem[]
  const techBase = t('home.techBase', { returnObjects: true }) as NamedItem[]

  return (
    <>
      <Seo title={t('seo.home.title')} description={t('seo.home.description')} />

      {/* HERO */}
      <section className="section-hero-bg">
        <div className="container-page grid-hero" style={{ paddingTop: 80 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 24, paddingBottom: 80 }}>
            <h1 className="h1-hero" style={{ margin: 0, fontSize: 52, lineHeight: 1.08, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>
              {t('home.heroTitle')}{' '}
              <span style={{ color: '#29ABE2' }}>{t('home.heroTitleAccent')}</span>
            </h1>
            <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: '#93A3AE', maxWidth: 520 }}>
              {t('home.heroText1')}
            </p>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: '#5C6670', maxWidth: 520 }}>
              {t('home.heroText2')}
            </p>
            <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
              <Link to={withLang(lang, '/zayavka')} style={{ background: '#189BD5', color: '#FFFFFF', fontSize: 13, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', padding: '15px 28px', borderRadius: 3, transition: 'background .15s' }}>
                {t('common.submitApplication')}
              </Link>
            </div>
          </div>
          <div className="hero-img" style={{ alignSelf: 'stretch', display: 'flex', alignItems: 'flex-start' }}>
            <img
              src={`${WP_URL}/wp-content/uploads/2026/07/красивое_фото.png`}
              alt={t('home.heroTitle')}
              style={{ width: '100%', height: 420, objectFit: 'cover', display: 'block', borderRadius: '3px 3px 0 0' }}
            />
          </div>
        </div>

        {/* Статистика */}
        <div style={{ borderTop: '1px solid #262D35' }}>
          <div className="container-page stats-grid" style={{ padding: '28px 32px' }}>
            <div className="stats-item" style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
              <span className="stats-value" style={{ color: '#FFFFFF', fontSize: 26, fontWeight: 700, letterSpacing: '-.01em', overflowWrap: 'break-word' }}>{t('home.statFoundedYear')}</span>
              <span className="stats-label" style={{ color: '#5C6670', fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', overflowWrap: 'break-word' }}>{t('home.statFoundedLabel')}</span>
            </div>
            {[
              [t('home.statDirections', { count: directions.length }), t('home.statDirectionsLabel')],
              [t('home.statSites'), t('home.statSitesLabel')],
              [t('home.statRegister'), t('home.statRegisterLabel')],
            ].map(([val, sub]) => (
              <div key={val} className="stats-item" style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span className="stats-value" style={{ color: '#FFFFFF', fontSize: 26, fontWeight: 700, letterSpacing: '-.01em', overflowWrap: 'break-word' }}>{val}</span>
                <span className="stats-label" style={{ color: '#5C6670', fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', overflowWrap: 'break-word' }}>{sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* НАПРАВЛЕНИЯ */}
      <section className="section-pad">
        <div className="container-page">
          <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('home.directionsTitle')}</h2>
          <div className="grid-4">
            {directions.map((d, i) => (
              <Link key={d.slug} to={withLang(lang, `/napravleniya/${d.slug}`)} className="card card-link" style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 40 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#189BD5', letterSpacing: '.06em' }}>{String(i + 1).padStart(2, '0')}</span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span style={{ fontSize: 18, fontWeight: 650, color: '#171C22' }}>{d.name}</span>
                  {d.subtitle && <span style={{ fontSize: 13.5, lineHeight: 1.55, color: '#5C6670' }}>{d.subtitle}</span>}
                  <span style={{ fontSize: 13, color: '#189BD5', marginTop: 6 }}>{t('common.more')}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* О КОМПАНИИ */}
      <section className="section-pad">
        <div className="container-page grid-content">
          <div>
            <h2 className="h2-section" style={{ margin: '0 0 24px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('home.aboutTitle')}</h2>
            <img
              src={`${WP_URL}/wp-content/uploads/2026/07/Дизеля.jpg`}
              alt={t('home.aboutTitle')}
              style={{ width: '100%', height: 260, objectFit: 'cover', display: 'block', borderRadius: 3 }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 58 }}>
            {[t('home.aboutText1'), t('home.aboutText2')].map((p, i) => (
              <p key={i} style={{ margin: 0, fontSize: 15.5, lineHeight: 1.7, color: '#3A434D' }}>{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* РУКОВОДСТВО */}
      <section className="section-pad">
        <div className="container-page">
          <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('home.leadershipTitle')}</h2>
          <div className="grid-3">
            {leadership.map((l, i) => (
              <div key={i} className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <ImagePlaceholder height={300} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ fontSize: 17, fontWeight: 650, color: '#171C22' }}>{l.name}</span>
                  <span style={{ fontSize: 12.5, letterSpacing: '.08em', textTransform: 'uppercase', color: '#189BD5', fontWeight: 600 }}>{l.role}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ПРЕИМУЩЕСТВА */}
      <section className="section-pad">
        <div className="container-page">
          <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('home.advantagesTitle')}</h2>
          <div className="grid-3">
            {advantages.map(a => (
              <div key={a.title} className="card" style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 650, color: '#171C22' }}>{a.title}</span>
                <span style={{ fontSize: 13.5, lineHeight: 1.6, color: '#5C6670' }}>{a.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ТЕХНИЧЕСКАЯ БАЗА */}
      <section className="section-pad" style={{ paddingBottom: 88 }}>
        <div className="container-page">
          <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('home.techBaseTitle')}</h2>
          <div className="grid-3">
            {techBase.map(item => (
              <div key={item.title} className="card" style={{ padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <span style={{ fontSize: 16, fontWeight: 650, color: '#171C22' }}>{item.title}</span>
                <span style={{ fontSize: 13.5, lineHeight: 1.6, color: '#5C6670' }}>{item.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* МИССИЯ + Заявка — единый фон */}
      <section className="section-hero-bg" style={{ backgroundSize: 'auto 100%' }}>
        <div className="container-page" style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', padding: '80px 0 0' }}>
          <h2 style={{ margin: 0, fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#FFFFFF' }}>
            {t('home.missionTitle')} <span style={{ color: '#29ABE2' }}>{t('home.missionTitleAccent')}</span>.
          </h2>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.7, color: '#93A3AE' }}>
            {t('home.missionText')}
          </p>
        </div>

        <ContactFormSection showContact noBg />
      </section>

      <Footer />
    </>
  )
}
